import { AlignHorizontalLeft, AlignVerticalTop } from "@mui/icons-material";
import {
  Box,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useColorScheme,
  useTheme,
} from "@mui/material";
import {
  Background,
  Controls,
  Edge,
  MiniMap,
  Node,
  Panel,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesInitialized,
  useNodesState,
  useOnSelectionChange,
  useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import React, { useCallback, useEffect, useState } from "react";
import { StepChallenge } from "../../directus/collections";
import { MatchFilter } from "../../libs/statsFilters";
import { useEnigmaFlow } from "./convertChannelToEnigmaFlow";
import { useLayout } from "./layout";
import {
  EnigmaHintsNode,
  EnigmaInitNode,
  EnigmaManagedAnswerNode,
  EnigmaSolutionNode,
  EnigmaSuccessNode,
} from "./nodes";
import { LangSwitchOption } from "../../types/misc";
interface EnigmaFlowChartProps {
  cityEscapeId: string;
  challenge: StepChallenge | null | undefined;
  filters?: MatchFilter;
  lang: LangSwitchOption;
  width?: string | number;
  height?: string | number;
  direction?: "DOWN" | "UP" | "RIGHT" | "LEFT";
  className?: string;
  minimap?: boolean;
  controls?: boolean;
  defaultZoom?: number;
  onSelection?: (node: Node) => void;
}

// Node types
const nodeTypes = {
  enigmaInit: EnigmaInitNode,
  enigmaHints: EnigmaHintsNode,
  enigmaSolution: EnigmaSolutionNode,
  enigmaManagedAnswer: EnigmaManagedAnswerNode,
  enigmaSuccess: EnigmaSuccessNode,
};

// Internal component that uses ReactFlow hooks
const EnigmaFlowChartInternal: React.FC<EnigmaFlowChartProps> = ({
  cityEscapeId,
  challenge,
  filters,
  lang,
  width = "100%",
  height = 400,
  direction = "DOWN",
  className,
  minimap = false,
  controls: _controls = true, // Commented out for now as not used
  defaultZoom = 1,
  onSelection,
}) => {
  const theme = useTheme();
  const colorScheme = useColorScheme();
  const [flowDirection, setFlowDirection] = useState<
    "DOWN" | "UP" | "RIGHT" | "LEFT"
  >(direction);

  // State for expanded scenes
  const [expandedScenes, setExpandedScenes] = useState<Set<string>>(new Set());
  const [nodes, setNodes, onNodesChange] = useNodesState<
    Node<{ challenge: StepChallenge }>
  >([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const nodesInitialized = useNodesInitialized({ includeHiddenNodes: true });
  const layoutedNodes = useLayout(nodes, edges, {
    direction: flowDirection,
    nodesInitialized,
  });
  const { getNodes, updateNodeData } = useReactFlow();

  // Convert city escape data to flow chart format with proper sub-flows
  const cityEscapeFlow = useEnigmaFlow(cityEscapeId, challenge, {
    theme,
    direction: flowDirection,
  });

  useEffect(() => {
    setNodes(cityEscapeFlow.data?.nodes || []);
    setEdges(cityEscapeFlow.data?.edges || []);
  }, [cityEscapeFlow.data, setNodes, setEdges]);

  useEffect(() => {
    if (layoutedNodes.data) {
      setNodes(
        layoutedNodes.data.map((node) => ({
          ...node,
          data: { ...node.data, filters: filters, activeLang: lang },
        }))
      );
    }
  }, [layoutedNodes.data, getNodes, setNodes]);

  useEffect(() => {
    for (const node of getNodes()) {
      if (["expanded", "scene"].includes(node.type || "")) {
        const isExpanded = expandedScenes.has(node.id);
        updateNodeData(node.id, {
          isExpanded: isExpanded,
          isExpandable: !isExpanded,
        });
      }
    }
  }, [expandedScenes, setNodes]);

  useEffect(() => {
    for (const node of getNodes()) {
      if (node.data.activeLang !== lang) {
        updateNodeData(node.id, { activeLang: lang });
      }
    }
  }, [lang]);

  useEffect(() => {
    for (const node of getNodes()) {
      updateNodeData(node.id, { filters: filters });
    }
  }, [filters]);

  const onNodeClick = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      // Only handle scene nodes to expand/collapse their steps
      if (node.data.nodeType === "scene" && node.data.scene) {
        const sceneId = node.id;
        setExpandedScenes((prev) => {
          const newSet = new Set(prev);

          if (prev.has(sceneId)) {
            newSet.delete(sceneId); // Collapse
          } else {
            newSet.add(sceneId); // Expand
          }
          return newSet;
        });
      }
    },
    [setExpandedScenes]
  );

  const onSelectionChange = useCallback(
    ({ nodes }: { nodes: Node[]; edges: Edge[] }) => {
      onSelection?.(nodes[0]);
    },
    [onSelection]
  );

  useOnSelectionChange({
    onChange: onSelectionChange,
  });

  return (
    <Box
      className={className}
      sx={{
        width,
        height,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        backgroundColor: theme.palette.background.default,
      }}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodeClick={onNodeClick}
        nodeTypes={nodeTypes}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={true}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        defaultViewport={{ zoom: defaultZoom, x: 0, y: 0 }}
        fitViewOptions={{
          maxZoom: defaultZoom,
        }}
        colorMode={colorScheme.mode}
      >
        <Background id="enigma-flow-bg" />
        <Controls position="top-left" />

        {/* Instructions panel */}
        <Panel position="bottom-left">
          <Box
            sx={{
              backgroundColor: theme.palette.background.paper,
              padding: 1,
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: theme.shadows[1],
              maxWidth: 250,
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.7rem" }}
            >
              💡 Clicca su una scena per espandere/comprimere i suoi step
            </Typography>
          </Box>
        </Panel>

        {minimap && (
          <>
            <MiniMap
              position="bottom-right"
              style={{
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
              }}
              nodeColor={theme.palette.primary.main}
            />
            <Panel position="top-right">
              <Stack
                direction="row"
                spacing={1}
                alignItems="flex-end"
                justifyContent="flex-end"
              >
                <ToggleButtonGroup
                  value={flowDirection}
                  exclusive
                  onChange={(e, newAlignment) => setFlowDirection(newAlignment)}
                  aria-label="text alignment"
                >
                  <ToggleButton value="RIGHT" aria-label="left aligned">
                    <AlignHorizontalLeft />
                  </ToggleButton>
                  <ToggleButton value="DOWN" aria-label="centered">
                    <AlignVerticalTop />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>
              <Box
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  padding: 1,
                  borderRadius: 1,
                  fontSize: "12px",
                  color: theme.palette.text.secondary,
                }}
              >
                {cityEscapeFlow.data?.nodes.length} scene,
                {cityEscapeFlow.data?.edges.length} connessioni
              </Box>
            </Panel>
          </>
        )}
      </ReactFlow>
    </Box>
  );
};

// Main component wrapped with ReactFlowProvider
export const EnigmaFlowChart: React.FC<EnigmaFlowChartProps> = (props) => {
  return (
    <ReactFlowProvider>
      <EnigmaFlowChartInternal {...props} />
    </ReactFlowProvider>
  );
};

export default EnigmaFlowChart;
